// The chrome every screen sits inside: a body whose content is capped at a phone-to-tablet reading
// width and centred, plus the TabBar (bottom bar on a phone, left rail on a wide screen). A screen
// that needs the room (the lesson's two columns) asks for it with useWideShell.
import "./config";
import { createContext, useContext, useEffect, useState } from "react";
import { Column, Row } from "./layout";
import { TabBar } from "./TabBar";

const WideShell = createContext<(wide: boolean) => void>(() => undefined);

/** Widens the shell's content column while the calling screen is mounted (a laptop only). */
export function useWideShell(wide: boolean): void {
  const setWide = useContext(WideShell);
  useEffect(() => {
    setWide(wide);
    return () => {
      setWide(false);
    };
  }, [setWide, wide]);
}

export interface AppShellProps {
  children: React.ReactNode;
  /** Hides the tab bar for a focused flow (the scan camera view). */
  hideTabBar?: boolean;
}

export function AppShell({ children, hideTabBar = false }: AppShellProps): React.JSX.Element {
  const [wide, setWide] = useState(false);
  return (
    <Row
      flex={1}
      minHeight="100dvh"
      alignItems="stretch"
      backgroundColor="bg"
      flexDirection={{ default: "column", rail: "row-reverse" }}
    >
      {/* tabIndex -1: a client-side route change moves focus here (App.tsx), so a screen reader or
          keyboard user is told a navigation happened instead of focus resetting to <body>. A
          visible ring would only flash on every screen for mouse users. */}
      <Column
        render="main"
        data-app-main=""
        tabIndex={-1}
        flexGrow={1}
        flexShrink={0}
        flexBasis="auto"
        paddingTop={{ default: "max(16px, env(safe-area-inset-top))", rail: "7" }}
        paddingHorizontal={{ default: "5", rail: "8" }}
        paddingBottom={{ default: "7", rail: "8" }}
        outlineWidth={0}
      >
        <Column
          data-app-content=""
          width="100%"
          flexGrow={1}
          flexShrink={0}
          flexBasis="auto"
          alignSelf="center"
          maxWidth={wide ? { default: 600, rail: 1080 } : { default: 600, desk: 760 }}
        >
          <WideShell value={setWide}>{children}</WideShell>
        </Column>
      </Column>
      {hideTabBar ? null : <TabBar />}
    </Row>
  );
}
